import type { MapEntityReference } from '@assets/core/map';
import { CameraInteractionAction } from './types';
import type {
  DialogApi,
  MapApiCamera,
  MapApiField,
  MapPageDom,
  MapState,
  TooltipApi,
} from './types';

/**
 * Configures page-owned accessibility and visual affordances after a shared scene render.
 *
 * The shared renderer exposes stable semantic hooks only; the page retains ownership of
 * interactive labels, focus visuals and API-backed camera click capability.
 *
 * @param params Page DOM and loaded runtime state used to enrich the latest SVG root.
 * @returns Nothing. Mutates only renderer-owned SVG attributes and page-level CSS classes.
 */
export function synchronizeMapSceneAccessibility(params: {
  mapDom: MapPageDom;
  state: MapState;
}): void {
  if (params.state.mapData === null) {
    return;
  }

  for (const fieldElement of params.mapDom.canvas.querySelectorAll<SVGPolygonElement>(
    '[data-map-entity-kind="field"]',
  )) {
    const field = resolveField({
      mapData: params.state.mapData,
      fieldId: Number(fieldElement.dataset.mapEntityId),
    });
    if (field === null) {
      continue;
    }

    fieldElement.setAttribute('role', 'button');
    fieldElement.setAttribute('tabindex', '0');
    fieldElement.setAttribute('aria-label', `Участок № ${field.fieldNumber}`);
    fieldElement.removeAttribute('aria-hidden');
    fieldElement.style.outline = 'none';
    fieldElement.classList.add('cursor-pointer');
  }

  for (const cameraElement of params.mapDom.canvas.querySelectorAll<SVGGElement>(
    '[data-map-entity-kind="camera"]',
  )) {
    const camera = resolveCamera({
      mapData: params.state.mapData,
      mapPositionId: Number(cameraElement.dataset.mapEntityId),
    });
    if (camera === null) {
      continue;
    }

    if (params.state.hasHoverCapability || params.state.hasFinePointer) {
      cameraElement.classList.add(
        'transition',
        'duration-150',
        'ease-out',
        'hover:drop-shadow-md',
        'hover:-translate-y-[1px]',
      );
    }

    if (camera.interaction.isClickable) {
      cameraElement.classList.add('cursor-pointer');
    }
  }

  if (params.state.hasHoverCapability || params.state.hasFinePointer) {
    for (const cameraLabelElement of params.mapDom.canvas.querySelectorAll<SVGTextElement>(
      '[data-map-entity-kind="camera-label"]',
    )) {
      cameraLabelElement.classList.add('transition', 'duration-150', 'ease-out');
    }
  }
}

/**
 * Registers the one stable set of page-level map listeners.
 *
 * SVG roots can be replaced after successful retry or `replaceData`; delegating from the
 * server-rendered canvas prevents duplicate listeners and avoids a `MutationObserver`.
 *
 * @param params Page DOM, mutable interaction state, UI controller facades and renderer lookup.
 * @returns Nothing. Registers one persistent delegated listener set on server-rendered containers.
 */
export function registerMapInteractions(params: {
  mapDom: MapPageDom;
  state: MapState;
  tooltipApi: TooltipApi;
  dialogApi: DialogApi;
  reloadMap: () => Promise<void>;
  getEntityFromTarget: (target: EventTarget | null) => MapEntityReference | null;
}): void {
  const { mapDom, state, tooltipApi, dialogApi, reloadMap, getEntityFromTarget } = params;

  /**
   * Resolves the current API field from a renderer-owned event target.
   *
   * @param target Browser event target captured by a delegated canvas listener.
   * @returns Matching API field, or `null` for non-field targets and absent map data.
   */
  const resolveCurrentField = (target: EventTarget | null): MapApiField | null => {
    const entityReference = getEntityFromTarget(target);
    if (entityReference?.kind !== 'field' || state.mapData === null) {
      return null;
    }

    return resolveField({
      mapData: state.mapData,
      fieldId: entityReference.id,
    });
  };

  /**
   * Resolves the current API camera placement from a renderer-owned event target.
   *
   * @param target Browser event target captured by a delegated canvas listener.
   * @returns Matching API camera, or `null` for non-camera targets and absent map data.
   */
  const resolveCurrentCamera = (target: EventTarget | null): MapApiCamera | null => {
    const entityReference = getEntityFromTarget(target);
    if (entityReference?.kind !== 'camera' || state.mapData === null) {
      return null;
    }

    return resolveCamera({
      mapData: state.mapData,
      mapPositionId: entityReference.id,
    });
  };

  /**
   * Clears a page-owned field hover or focus stroke without altering the shared render model.
   *
   * @param fieldElement Focusable field polygon to reset.
   * @returns Nothing. Mutates only SVG presentation attributes.
   */
  const clearFieldStroke = (fieldElement: SVGPolygonElement): void => {
    fieldElement.removeAttribute('stroke-dasharray');
    fieldElement.setAttribute('stroke-width', '1');
  };

  /**
   * Restores the keyboard-only field focus stroke when the browser exposes `:focus-visible` state.
   *
   * @param fieldElement Focusable field polygon whose visual focus state is synchronized.
   * @returns Nothing. Mutates only SVG presentation attributes.
   */
  const synchronizeKeyboardFocusAppearance = (fieldElement: SVGPolygonElement): void => {
    if (!fieldElement.matches(':focus-visible')) {
      clearFieldStroke(fieldElement);
      return;
    }

    fieldElement.setAttribute('stroke-width', '3');
    fieldElement.setAttribute('stroke-dasharray', '4 2');
  };

  /**
   * Opens the page-owned payment dialog for one API field and preserves its exact SVG opener.
   *
   * @param fieldElement Activated polygon retained for accessible focus restoration on dialog close.
   * @param field Loaded API field supplying payment dialog content.
   * @returns Nothing. Clears local hover UI and delegates dialog lifecycle to `DialogApi`.
   */
  const activateField = (fieldElement: SVGPolygonElement, field: MapApiField): void => {
    state.hoveredFieldId = null;
    clearFieldStroke(fieldElement);
    tooltipApi.hide();
    dialogApi.open({
      fieldNumber: field.fieldNumber,
      dialogData: field.dialog,
      opener: fieldElement,
    });
  };

  /**
   * Executes the page-authorized action for one camera without moving policy into the renderer.
   *
   * @param camera Loaded API camera providing the access and navigation decision.
   * @returns Nothing. May open the auth dialog or a new camera page tab.
   */
  const activateCamera = (camera: MapApiCamera): void => {
    if (!camera.interaction.isClickable) {
      return;
    }

    if (camera.interaction.action === CameraInteractionAction.ShowLoginRequiredDialog) {
      dialogApi.openAuthRequired();
      return;
    }

    if (
      camera.interaction.action === CameraInteractionAction.OpenCameras &&
      camera.interaction.targetUrl !== null
    ) {
      window.open(camera.interaction.targetUrl, '_blank');
    }
  };

  mapDom.errorRetryButton.addEventListener('click', () => {
    void reloadMap();
  });

  mapDom.canvas.addEventListener('pointerover', (event) => {
    if (
      event.pointerType !== 'mouse' ||
      !state.hasHoverCapability ||
      !state.hasFinePointer ||
      dialogApi.isOpen()
    ) {
      return;
    }

    const entityElement = resolveEntityElement(event.target);
    if (
      entityElement !== null &&
      event.relatedTarget instanceof Node &&
      entityElement.contains(event.relatedTarget)
    ) {
      return;
    }

    const field = resolveCurrentField(event.target);
    const fieldElement = resolveFieldElement(event.target);
    if (field !== null && fieldElement !== null) {
      state.hoveredCameraMapPositionId = null;
      state.hoveredFieldId = field.id;
      fieldElement.setAttribute('stroke-width', '2.5');
      tooltipApi.showField({
        tooltipData: field.tooltip,
        pointerX: event.clientX,
        pointerY: event.clientY,
      });
      return;
    }

    const camera = resolveCurrentCamera(event.target);
    if (camera === null) {
      return;
    }

    state.hoveredFieldId = null;
    state.hoveredCameraMapPositionId = camera.mapPositionId;
    setCameraLabelHoverLift({
      mapDom,
      mapPositionId: camera.mapPositionId,
      lifted: true,
    });
    tooltipApi.showCamera({
      tooltipData: camera.tooltip,
      pointerX: event.clientX,
      pointerY: event.clientY,
    });
  });

  mapDom.canvas.addEventListener('pointermove', (event) => {
    if (event.pointerType !== 'mouse' || dialogApi.isOpen()) {
      return;
    }

    const field = resolveCurrentField(event.target);
    if (field !== null && state.hoveredFieldId === field.id) {
      tooltipApi.move({ pointerX: event.clientX, pointerY: event.clientY });
      return;
    }

    const camera = resolveCurrentCamera(event.target);
    if (camera !== null && state.hoveredCameraMapPositionId === camera.mapPositionId) {
      tooltipApi.move({ pointerX: event.clientX, pointerY: event.clientY });
    }
  });

  mapDom.canvas.addEventListener('pointerout', (event) => {
    if (event.pointerType !== 'mouse') {
      return;
    }

    const entityElement = resolveEntityElement(event.target);
    if (
      entityElement !== null &&
      event.relatedTarget instanceof Node &&
      entityElement.contains(event.relatedTarget)
    ) {
      return;
    }

    const field = resolveCurrentField(event.target);
    const fieldElement = resolveFieldElement(event.target);
    if (field !== null && fieldElement !== null && state.hoveredFieldId === field.id) {
      state.hoveredFieldId = null;
      clearFieldStroke(fieldElement);
      tooltipApi.hide();
      queueMicrotask(() => synchronizeKeyboardFocusAppearance(fieldElement));
      return;
    }

    const camera = resolveCurrentCamera(event.target);
    if (camera !== null && state.hoveredCameraMapPositionId === camera.mapPositionId) {
      state.hoveredCameraMapPositionId = null;
      setCameraLabelHoverLift({
        mapDom,
        mapPositionId: camera.mapPositionId,
        lifted: false,
      });
      tooltipApi.hide();
    }
  });

  mapDom.canvas.addEventListener('pointerdown', (event) => {
    const fieldElement = resolveFieldElement(event.target);
    if (fieldElement !== null) {
      clearFieldStroke(fieldElement);
    }
  });

  mapDom.canvas.addEventListener('focusin', (event) => {
    const fieldElement = resolveFieldElement(event.target);
    if (fieldElement !== null) {
      synchronizeKeyboardFocusAppearance(fieldElement);
    }
  });

  mapDom.canvas.addEventListener('focusout', (event) => {
    const fieldElement = resolveFieldElement(event.target);
    if (fieldElement !== null) {
      clearFieldStroke(fieldElement);
    }
  });

  mapDom.canvas.addEventListener('click', (event) => {
    const field = resolveCurrentField(event.target);
    const fieldElement = resolveFieldElement(event.target);
    if (field !== null && fieldElement !== null) {
      activateField(fieldElement, field);
      return;
    }

    const camera = resolveCurrentCamera(event.target);
    if (camera !== null) {
      activateCamera(camera);
    }
  });

  mapDom.canvas.addEventListener('keydown', (event) => {
    if (event.key !== 'Enter' && event.key !== ' ') {
      return;
    }

    const field = resolveCurrentField(event.target);
    const fieldElement = resolveFieldElement(event.target);
    if (field === null || fieldElement === null) {
      return;
    }

    event.preventDefault();
    activateField(fieldElement, field);
  });
}

/**
 * Resolves one API field by the canonical identifier exposed by the renderer semantic hook.
 *
 * @param params Loaded map DTO and canonical field ID.
 * @returns Matching field, or `null` when the hook no longer maps to current API data.
 */
function resolveField(params: {
  mapData: NonNullable<MapState['mapData']>;
  fieldId: number;
}): MapApiField | null {
  return params.mapData.fields.find((field) => field.id === params.fieldId) ?? null;
}

/**
 * Resolves one API camera by the canonical map-position ID exposed by the renderer semantic hook.
 *
 * @param params Loaded map DTO and canonical physical camera-placement ID.
 * @returns Matching camera placement, or `null` when the hook no longer maps to current API data.
 */
function resolveCamera(params: {
  mapData: NonNullable<MapState['mapData']>;
  mapPositionId: number;
}): MapApiCamera | null {
  return (
    params.mapData.cameras.find((camera) => camera.mapPositionId === params.mapPositionId) ?? null
  );
}

/**
 * Finds the closest renderer semantic entity hook from a delegated canvas event target.
 *
 * @param target Browser event target captured by a map canvas listener.
 * @returns Closest SVG entity element, or `null` when the target is not an SVG semantic entity.
 */
function resolveEntityElement(target: EventTarget | null): SVGElement | null {
  if (!(target instanceof Element)) {
    return null;
  }

  const entityElement = target.closest<SVGElement>('[data-map-entity-kind]');
  return entityElement instanceof SVGElement ? entityElement : null;
}

/**
 * Finds the polygon field hook when the event target belongs to a rendered field.
 *
 * @param target Browser event target captured by a map canvas listener.
 * @returns Field polygon, or `null` for camera, label, street and non-SVG targets.
 */
function resolveFieldElement(target: EventTarget | null): SVGPolygonElement | null {
  const entityElement = resolveEntityElement(target);
  return entityElement instanceof SVGPolygonElement &&
    entityElement.dataset.mapEntityKind === 'field'
    ? entityElement
    : null;
}

/**
 * Applies or removes a camera-label hover lift through the renderer semantic hook.
 *
 * @param params Page DOM, camera placement ID and required lifted state.
 * @returns Nothing. Mutates only the matching label CSS class when that label is rendered.
 */
function setCameraLabelHoverLift(params: {
  mapDom: MapPageDom;
  mapPositionId: number;
  lifted: boolean;
}): void {
  const cameraLabelElement = Array.from(
    params.mapDom.canvas.querySelectorAll<SVGTextElement>('[data-map-entity-kind="camera-label"]'),
  ).find((element) => element.dataset.mapEntityId === String(params.mapPositionId));

  cameraLabelElement?.classList.toggle('-translate-y-[1px]', params.lifted);
}
